import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_URL } from "config";

const REPORT_COLUMNS = [
  { key: "completed", label: "Completes" },
  { key: "security_terminate", label: "Security Terminate" },
  { key: "quota_full", label: "Quota Full" },
  { key: "total_hits", label: "Total Hits" },
  { key: "client_rejected", label: "Client Rejected" },
];

const emptyTotals = () =>
  REPORT_COLUMNS.reduce((totals, { key }) => ({ ...totals, [key]: 0 }), {});

const emptyPagination = { current_page: 1, last_page: 1, per_page: 50, total: 0, from: 0, to: 0 };

const compactControlSx = {
  width: { xs: "100%", sm: 190 },
  minWidth: 0,
};

const compactTableCellSx = {
  fontSize: "12px",
  py: 0.8,
  px: 1,
  borderBottom: "1px solid #edf2f7",
  whiteSpace: "nowrap",
};

function StatusCountsReport({ reportType }) {
  const isClientReport = reportType === "client";
  const isVendorReport = reportType === "vendor";
  const [filters, setFilters] = useState({ client: [], vendor: [], startDate: "", endDate: "" });
  const [sorting, setSorting] = useState({ key: "total_hits", direction: "desc" });
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityOptionsLoading, setEntityOptionsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(emptyTotals);
  const [pagination, setPagination] = useState(emptyPagination);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [exportError, setExportError] = useState("");

  const title = isClientReport ? "Client Data" : "Vendor Data";
  const entityLabel = isClientReport ? "Client" : "Vendor";

  const buildParams = useCallback(
    (page = 1) => {
      const params = new URLSearchParams({ page: String(page), per_page: "50" });
      if (filters.startDate) params.set("start_date", filters.startDate);
      if (filters.endDate) params.set("end_date", filters.endDate);
      if (isClientReport) {
        filters.client.forEach(({ value }) => params.append("client_ids[]", String(value)));
      }
      if (isVendorReport) {
        filters.vendor.forEach(({ value }) => params.append("vendor_ids[]", String(value)));
      }
      params.set("sort_by", sorting.key);
      params.set("sort_direction", sorting.direction);
      return params;
    },
    [
      filters.client,
      filters.endDate,
      filters.startDate,
      filters.vendor,
      isClientReport,
      isVendorReport,
      sorting.direction,
      sorting.key,
    ]
  );

  const loadReport = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_URL}/reports/${reportType}-data?${buildParams(page).toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load report data.");
        }

        setRows(result.data?.rows || []);
        setTotals(result.data?.totals || emptyTotals());
        setPagination(result.data?.pagination || emptyPagination);
      } catch (requestError) {
        setRows([]);
        setTotals(emptyTotals());
        setPagination(emptyPagination);
        setError(requestError.message || "Unable to load report data.");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, reportType]
  );

  const loadEntityOptions = useCallback(async () => {
    setEntityOptionsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (isClientReport) {
        const params = new URLSearchParams({ project_filter: "1", project_tab: "live" });
        const response = await fetch(`${API_URL}/projects/clients?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();

        if (!response.ok || result.success !== 1) {
          throw new Error(result.message || "Unable to load clients.");
        }

        setEntityOptions(
          (Array.isArray(result.clients) ? result.clients : [])
            .filter((client) => client?.value && (client?.name || client?.client_name || client?.label))
            .map((client) => ({
              value: client.value,
              label: client.name || client.client_name || client.label,
            }))
        );
        return;
      }

      const vendors = [];
      let page = 1;
      let lastPage = 1;
      do {
        const params = new URLSearchParams({ page: String(page), per_page: "500", order_by: "name", order: "ASC" });
        const response = await fetch(`${API_URL}/vendors?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();

        if (!response.ok || result.success !== 1) {
          throw new Error(result.message || "Unable to load vendors.");
        }

        const vendorPage = result.vendors || {};
        vendors.push(...(Array.isArray(vendorPage.data) ? vendorPage.data : []));
        lastPage = Number(vendorPage.last_page || 1);
        page += 1;
      } while (page <= lastPage);

      setEntityOptions(
        vendors
          .filter((vendor) => vendor?.id && (vendor?.name || vendor?.company_name || vendor?.company))
          .map((vendor) => ({
            value: vendor.id,
            label: vendor.name || vendor.company_name || vendor.company,
          }))
      );
    } catch (requestError) {
      setEntityOptions([]);
    } finally {
      setEntityOptionsLoading(false);
    }
  }, [isClientReport]);

  useEffect(() => {
    loadReport(1);
  }, [loadReport]);

  useEffect(() => {
    loadEntityOptions();
  }, [loadEntityOptions]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const changePage = (page) => {
    if (page < 1 || page > pagination.last_page || page === pagination.current_page) return;
    loadReport(page);
  };

  const changeSorting = (key) => {
    setSorting((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  const exportReport = async () => {
    setExporting(true);
    setExportError("");

    try {
      const token = localStorage.getItem("authToken");
      const params = buildParams(1);
      params.delete("page");
      params.delete("per_page");
      const response = await fetch(
        `${API_URL}/reports/${reportType}-data/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        let message = "Unable to export report data.";
        try {
          const result = await response.json();
          message = result.message || message;
        } catch {
          // Keep the fallback for non-JSON server errors.
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = fileNameMatch?.[1] || `${reportType}-data-report.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setExportError(requestError.message || "Unable to export report data.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={2}>
        <Card>
          <SoftBox p={{ xs: 1.5, md: 2 }}>
            <SoftTypography variant="h5" fontWeight="medium" mb={1.5}>
              {title}
            </SoftTypography>

            <SoftBox display="flex" flexWrap="wrap" alignItems="flex-end" gap={1} mb={1.5}>
              {(isClientReport || isVendorReport) && (
                <SoftBox sx={{ ...compactControlSx, width: { xs: "100%", sm: 250 } }}>
                  <SoftSelect
                    size="small"
                    compactMenu
                    placeholder={entityLabel}
                    value={isClientReport ? filters.client : filters.vendor}
                    options={entityOptions}
                    isLoading={entityOptionsLoading}
                    isMulti
                    isSearchable
                    isClearable
                    closeMenuOnSelect={false}
                    onChange={(value) =>
                      updateFilter(isClientReport ? "client" : "vendor", Array.isArray(value) ? value : [])
                    }
                  />
                </SoftBox>
              )}
              <SoftBox sx={compactControlSx}>
                <SoftInput
                  size="small"
                  type="datetime-local"
                  value={filters.startDate}
                  onChange={(event) => updateFilter("startDate", event.target.value)}
                  inputProps={{ "aria-label": "Start date and time" }}
                />
              </SoftBox>
              <SoftBox sx={compactControlSx}>
                <SoftInput
                  size="small"
                  type="datetime-local"
                  value={filters.endDate}
                  onChange={(event) => updateFilter("endDate", event.target.value)}
                  inputProps={{ "aria-label": "End date and time" }}
                />
              </SoftBox>
              <SoftButton
                size="small"
                variant="gradient"
                color="info"
                onClick={exportReport}
                disabled={exporting}
              >
                <Icon sx={{ mr: 0.5 }}>download</Icon>
                {exporting ? "Exporting..." : "Export Excel"}
              </SoftButton>
            </SoftBox>

            {exportError && (
              <SoftTypography color="error" variant="caption" display="block" mb={1}>
                {exportError}
              </SoftTypography>
            )}

            <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.75} mb={1}>
              <SoftTypography variant="caption" color="text">
                Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} entries
              </SoftTypography>
              <SoftTypography variant="caption" color="text">50 per page</SoftTypography>
            </SoftBox>

            {loading ? (
              <SoftBox py={5} display="flex" justifyContent="center"><CircularProgress color="info" /></SoftBox>
            ) : error ? (
              <SoftTypography color="error" variant="button">{error}</SoftTypography>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "hidden", width: "100%", borderTop: "1px solid #edf2f7" }}>
                  <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell width="30%" sx={{ ...compactTableCellSx, fontWeight: 600 }}>{entityLabel}</TableCell>
                        {REPORT_COLUMNS.map(({ key, label }) => (
                          <TableCell key={key} width="14%" align="right" sx={{ ...compactTableCellSx, fontWeight: 600, whiteSpace: "normal", lineHeight: 1.15 }}>
                            <TableSortLabel
                              active={sorting.key === key}
                              direction={sorting.key === key ? sorting.direction : "desc"}
                              onClick={() => changeSorting(key)}
                            >
                              {label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <TableRow key={`${row.name}-${index}`} hover>
                          <TableCell sx={{ ...compactTableCellSx, overflow: "hidden", textOverflow: "ellipsis" }} title={row.name}>{row.name}</TableCell>
                          {REPORT_COLUMNS.map(({ key }) => <TableCell key={key} align="right" sx={compactTableCellSx}>{Number(row[key] || 0)}</TableCell>)}
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: "#f8fafc", "& td": { fontWeight: 700 } }}>
                        <TableCell sx={compactTableCellSx}>Total</TableCell>
                        {REPORT_COLUMNS.map(({ key }) => <TableCell key={key} align="right" sx={compactTableCellSx}>{Number(totals[key] || 0)}</TableCell>)}
                      </TableRow>
                      {rows.length === 0 && (
                        <TableRow><TableCell colSpan={REPORT_COLUMNS.length + 1} align="center" sx={{ ...compactTableCellSx, py: 3 }}>No data found for the selected filters.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pagination.last_page > 1 && (
                  <SoftBox display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                    <SoftPagination item onClick={() => changePage(1)} disabled={pagination.current_page === 1} sx={{ width: 26, minWidth: 26, height: 26, p: 0 }}><Icon sx={{ fontSize: "14px !important" }}>first_page</Icon></SoftPagination>
                    <SoftPagination item onClick={() => changePage(pagination.current_page - 1)} disabled={pagination.current_page === 1} sx={{ width: 26, minWidth: 26, height: 26, p: 0 }}><Icon sx={{ fontSize: "14px !important" }}>chevron_left</Icon></SoftPagination>
                    <SoftTypography variant="caption" mx={1}>{pagination.current_page} / {pagination.last_page}</SoftTypography>
                    <SoftPagination item onClick={() => changePage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page} sx={{ width: 26, minWidth: 26, height: 26, p: 0 }}><Icon sx={{ fontSize: "14px !important" }}>chevron_right</Icon></SoftPagination>
                    <SoftPagination item onClick={() => changePage(pagination.last_page)} disabled={pagination.current_page === pagination.last_page} sx={{ width: 26, minWidth: 26, height: 26, p: 0 }}><Icon sx={{ fontSize: "14px !important" }}>last_page</Icon></SoftPagination>
                  </SoftBox>
                )}
              </>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

StatusCountsReport.propTypes = {
  reportType: PropTypes.oneOf(["client", "vendor"]).isRequired,
};

export default StatusCountsReport;
